import * as React from 'react';
import { Journals } from '../../types/Journals.types';
import { useSearch } from '../../hooks/api/useSearch';
import { useAuthContext } from '../../contexts/AuthContext';
import {
  getSortedData,
  usePersistentSorting,
} from '../../hooks/usePersistentSorting';
import { SearchHeader } from './components/SearchHeader';
import { JournalsTable } from './components/JournalsTable';
import { useFilterSelection } from './hooks/useFilterSelection';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle } from '../../components/PageTitle';
import { InternalSearchFilter, InternalSearchFilterKeys } from './types';

export default function InternalSearch() {
  const [journals, setJournals] = useState<Journals[]>();
  const [search, setSearch] = useState<string>('');
  const [totalSearch, setTotalSearch] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { order, orderBy, setOrder, setOrderBy } =
    usePersistentSorting<Journals>('agreement_id', 'journals');

  const { searchJournals } = useSearch();
  const { t } = useTranslation();

  const [filters, setFilters] = useState<InternalSearchFilter>({
    licenses: [],
    publishingModels: [],
    payments: [],
    imprints: [],
    statuses: [],
  });

  const { account } = useAuthContext();

  const { selectedFilters, setSelectedFilter, clearAllFilters } =
    useFilterSelection();

  const handleToggleFilter = (
    type: InternalSearchFilterKeys,
    value: string
  ) => {
    setSelectedFilter(type, value);
    // Clear current results to avoid screen reader announcing stale rows
    setJournals([]);
    setTotalSearch(0);
  };
  const handleSearch = (term: string) => {
    setSearch(term);
  };

  useEffect(() => {
    const getJournals = async () => {
      setIsLoading(true);
      try {
        const {
          data,
          licenses,
          imprints,
          payments,
          publishingModels,
          statuses,
        } = await searchJournals(
          search,
          selectedFilters.licenses,
          selectedFilters.publishingModels,
          selectedFilters.payments,
          selectedFilters.imprints,
          selectedFilters.statuses
        );
        const journals: Journals[] = data?.data;
        setJournals(journals);
        setTotalSearch(data?.total_count || 0);
        setFilters({
          licenses,
          imprints,
          payments,
          publishingModels,
          statuses,
        });
      } catch {
        setJournals([]);
      } finally {
        setIsLoading(false);
      }
    };
    getJournals();
  }, [
    search,
    account,
    selectedFilters.licenses,
    selectedFilters.publishingModels,
    selectedFilters.payments,
    selectedFilters.imprints,
    selectedFilters.statuses,
    searchJournals,
  ]);

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof Journals
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedJournals = useMemo(
    () => getSortedData(journals, orderBy, order),
    [journals, order, orderBy]
  );

  const hasFiltersApplied = Object.values(selectedFilters).some(
    (arr) => Array.isArray(arr) && arr.length > 0
  );

  return (
    <>
      <PageTitle title={t('publicSearch.internalSearch') as string} />
      <SearchHeader
        onSearch={handleSearch}
        totalResults={totalSearch}
        lastSearchTerm={search}
        isLoading={isLoading}
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={clearAllFilters}
      />
      <JournalsTable
        journals={sortedJournals}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
        filters={filters}
        selectedFilters={selectedFilters}
        hasFiltersApplied={hasFiltersApplied}
        lastSearchTerm={search}
        isLoading={isLoading}
        onToggleFilter={handleToggleFilter}
      />
    </>
  );
}
